import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Search, User } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

const LINKS = [
  { to: "/services", label: "Nos services" },
  { to: "/comment-ca-marche", label: "Comment ça marche" },
  { to: "/realisations", label: "Nos réalisations" },
  { to: "/modeles", label: "Nos modèles" },
  { to: "/tarifs", label: "Tarifs" },
  { to: "/conseils", label: "Conseils" },
  { to: "/nous-rejoindre", label: "Nous rejoindre" },
  { to: "/a-propos", label: "À propos" },
];

// Espaces accessibles depuis « Connexion » (CozyHome Central reste volontairement hors de ce menu public)
const SPACES = [
  { to: "/acces-phase3?role=client", label: "Espace client", sub: "Accéder au suivi de mon projet", color: "#3C4D3A", key: "client" },
  { to: "/acces-phase3?role=partner", label: "Espace partenaire", sub: "Accéder à mon espace professionnel", color: "#284E55", key: "partenaire" },
  { to: "/acces-phase3?role=company", label: "Espace entreprise", sub: "Accéder à mes chantiers", color: "#8A5541", key: "entreprise" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [connexionOpen, setConnexionOpen] = useState(false);
  const connexionRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Ferme le panneau Connexion à chaque navigation et avec la touche Échap
  useEffect(() => {
    setConnexionOpen(false);
    setOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const close = (event) => event.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", close);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", close);
    };
  }, [open]);

  useEffect(() => {
    if (!connexionOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setConnexionOpen(false);
    };
    const onPointerDown = (e) => {
      if (connexionRef.current && !connexionRef.current.contains(e.target)) {
        setConnexionOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [connexionOpen]);

  return (
    <>
      <header
        data-testid="navbar"
        className="fixed inset-x-0 top-0 z-50 border-b border-[#EDEAE3] bg-[#FEFDF9]/90 backdrop-blur-md"
      >
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-2 px-4 sm:px-6 lg:gap-3 lg:px-5 xl:px-8">
        <Link to="/" data-testid="nav-logo" className="flex shrink-0 items-center">
          <Logo className="h-8 w-auto max-w-[110px] sm:h-9 sm:max-w-[125px]" />
        </Link>

        <nav className="hidden items-center gap-2 lg:flex xl:gap-4">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              data-testid={`nav-link-${l.to.slice(1)}`}
              to={l.to}
              className={`relative whitespace-nowrap text-[13px] font-semibold transition-colors duration-300 hover:text-cognac ${
                location.pathname === l.to
                  ? "text-cognac after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-full after:rounded-full after:bg-cognac"
                  : "text-navy/80"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden shrink-0 items-center gap-2 lg:flex xl:gap-2.5">
          <button
            data-testid="nav-search"
            onClick={() => toast.info("La recherche arrive bientôt.")}
            aria-label="Rechercher"
            className="rounded-full p-2 text-navy/60 transition-colors duration-300 hover:bg-sage/10 hover:text-navy"
          >
            <Search className="h-4 w-4" />
          </button>
          <div className="relative" ref={connexionRef}>
            <button
              data-testid="nav-connexion"
              onClick={() => setConnexionOpen((v) => !v)}
              aria-expanded={connexionOpen}
              aria-haspopup="menu"
              className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-sage px-3.5 py-1.5 text-[13px] font-bold text-navy transition-colors duration-300 hover:bg-sage hover:text-white xl:px-4"
            >
              <User className="h-3.5 w-3.5" />
              Connexion
            </button>
            <AnimatePresence>
              {connexionOpen && (
                <motion.div
                  data-testid="connexion-panel"
                  role="menu"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="absolute right-0 z-50 mt-3 w-80 rounded-2xl border border-[#EDEAE3] bg-white p-2.5 shadow-xl shadow-navy/5"
                >
                  {SPACES.map((s) => (
                    <Link
                      key={s.key}
                      to={s.to}
                      data-testid={`connexion-option-${s.key}`}
                      role="menuitem"
                      className="group flex items-center gap-3.5 rounded-xl px-4 py-3.5 transition-colors duration-200 hover:bg-cream"
                    >
                      <span
                        className="h-9 w-1.5 shrink-0 rounded-full"
                        style={{ backgroundColor: s.color }}
                        aria-hidden="true"
                      />
                      <span className="min-w-0">
                        <span className="block text-[11px] font-bold uppercase tracking-wider" style={{ color: s.color }}>
                          {s.label}
                        </span>
                        <span className="mt-0.5 block text-sm font-medium text-stone-500 transition-colors duration-200 group-hover:text-navy">
                          {s.sub}
                        </span>
                      </span>
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <button
          type="button"
          data-testid="nav-burger"
          onClick={() => setOpen((value) => !value)}
          aria-label="Ouvrir le menu"
          aria-expanded={open}
          aria-controls="public-mobile-menu"
          className="rounded-full border border-navy/20 p-2.5 text-navy lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>
      </header>

      <motion.div
            id="public-mobile-menu"
            data-testid="nav-mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-hidden={!open}
            initial={false}
            animate={open ? { opacity: 1, x: 0 } : { opacity: 0, x: "100%" }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            className={`fixed inset-0 z-[110] overflow-y-auto bg-[#FEFDF9] lg:hidden ${open ? "visible pointer-events-auto" : "invisible pointer-events-none"}`}
          >
            <div className="flex h-20 items-center justify-between px-5">
              <Logo className="h-11 w-auto max-w-[180px]" />
              <button
                data-testid="nav-mobile-close"
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Fermer le menu"
                className="rounded-full border border-navy/20 p-2.5 text-navy"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-col gap-2 px-8 pt-6">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link
                    data-testid={`nav-mobile-link-${l.to.slice(1)}`}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className={`block text-left font-display text-3xl font-medium transition-colors duration-300 hover:text-cognac sm:text-4xl ${
                      location.pathname === l.to ? "text-cognac" : "text-navy"
                    }`}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}

              {/* Connexion — 3 espaces */}
              <motion.div
                data-testid="nav-mobile-connexion"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-7 border-t border-[#EDEAE3] pt-5"
              >
                <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone-400">Connexion</p>
                <div className="mt-3 flex flex-col gap-1.5">
                  {SPACES.map((s) => (
                    <Link
                      key={s.key}
                      to={s.to}
                      data-testid={`nav-mobile-connexion-${s.key}`}
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors duration-200 hover:bg-cream"
                    >
                      <span
                        className="h-7 w-1.5 shrink-0 rounded-full"
                        style={{ backgroundColor: s.color }}
                        aria-hidden="true"
                      />
                      <span className="min-w-0">
                        <span className="block text-[11px] font-bold uppercase tracking-wider" style={{ color: s.color }}>
                          {s.label}
                        </span>
                        <span className="block text-xs font-medium text-stone-500">{s.sub}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
                className="mb-8 mt-7 flex flex-col gap-3"
              >
                <button
                  data-testid="nav-mobile-estimate"
                  onClick={() => { setOpen(false); navigate("/estimer"); }}
                  className="rounded-full bg-navy px-8 py-4 text-sm font-bold text-white"
                >
                  Parler de mon projet
                </button>
                <Link
                  data-testid="nav-mobile-suivi"
                  to="/acces-phase3?role=client"
                  onClick={() => setOpen(false)}
                  className="rounded-full border border-navy/25 px-8 py-4 text-center text-sm font-bold text-navy"
                >
                  Espace client
                </Link>
              </motion.div>
            </nav>
          </motion.div>
    </>
  );
};
